import React, { useState, useRef, useEffect, useMemo } from 'react';

function CustomDatePicker({ value, onChange, placeholder, className, style, disabled, disabledDateRanges = [] }) {
  const [isOpen, setIsOpen] = useState(false);

  const parseValue = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    const parts = val.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    return null;
  };

  const [selectedDate, setSelectedDate] = useState(value ? parseValue(value) : null);
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const datePickerRef = useRef(null);
  const [inModal, setInModal] = useState(false);

  useEffect(() => {
    setInModal(!!datePickerRef.current?.closest('.modal-body, .confirmation-popup'));
  }, []);

  useEffect(() => {
    if (isOpen && inModal) {
      datePickerRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, inModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      const parsed = parseValue(value);
      setSelectedDate(parsed);
      if (parsed) {
        setCurrentMonth(parsed);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizedDisabledRanges = useMemo(() => {
    if (!Array.isArray(disabledDateRanges)) return [];
    return disabledDateRanges
      .map((range) => {
        if (!range || !range.startDate) return null;
        const start = new Date(range.startDate);
        start.setHours(0, 0, 0, 0);
        let end = null;
        if (range.endDate) {
          end = new Date(range.endDate);
          end.setHours(0, 0, 0, 0);
        }
        return { start, end };
      })
      .filter((range) => range);
  }, [disabledDateRanges]);

  const isDateDisabled = (date) => {
    if (!date || normalizedDisabledRanges.length === 0) return false;
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    return normalizedDisabledRanges.some(({ start, end }) => {
      if (!start) return false;
      if (end) {
        return target >= start && target <= end;
      }
      return target >= start;
    });
  };

  const handleDateSelect = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today || isDateDisabled(date)) {
      return;
    }

    setSelectedDate(date);
    onChange(formatDate(date));
    setIsOpen(false);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isSelected =
        selectedDate && date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();

      const isToday = date.toDateString() === new Date().toDateString();
      const isPastDate = date < today;
      const isBlocked = isDateDisabled(date);
      const isUnavailable = isPastDate || isBlocked;

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isUnavailable ? 'disabled' : ''} ${isBlocked ? 'blocked' : ''}`}
          onClick={() => !isUnavailable && handleDateSelect(date)}
          style={{ cursor: isUnavailable ? 'not-allowed' : 'pointer' }}
          title={isBlocked ? 'Asset unavailable for this date' : undefined}>
          {day}
        </div>
      );
    }

    return (
      // Inside a modal an absolutely positioned popup gets clipped by the scrollable
      // modal body, so render it in-flow there to let the modal grow with it.
      <div className="custom-datepicker-popup" style={inModal ? { position: 'static' } : undefined}>
        <div className="calendar-header">
          <button type="button" className="nav-button" onClick={() => navigateMonth(-1)}>
            ‹
          </button>
          <span className="month-year">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <button type="button" className="nav-button" onClick={() => navigateMonth(1)}>
            ›
          </button>
        </div>

        <div className="calendar-weekdays">
          <div className="weekday">Sun</div>
          <div className="weekday">Mon</div>
          <div className="weekday">Tue</div>
          <div className="weekday">Wed</div>
          <div className="weekday">Thu</div>
          <div className="weekday">Fri</div>
          <div className="weekday">Sat</div>
        </div>

        <div className="calendar-grid">{days}</div>

        <div className="calendar-footer">
          <button type="button" className="footer-button" onClick={() => handleDateSelect(new Date())}>
            Today
          </button>
          <button
            type="button"
            className="footer-button"
            onClick={() => {
              setSelectedDate(null);
              onChange('');
              setIsOpen(false);
            }}>
            Clear
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="custom-datepicker-container" ref={datePickerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input type="text" value={value || ''} placeholder={placeholder} className={className} style={style} onClick={handleInputClick} readOnly disabled={disabled} />
        <div
          className="calendar-icon"
          onClick={handleInputClick}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            color: '#ffffff',
            fontSize: '16px',
          }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4 .5a.5.5 0 0 0-1 0V1H2a2 2 0 0 0-2 2v1h16V3a2 2 0 0 0-2-2h-1V.5a.5.5 0 0 0-1 0V1H4zM16 14V5H0v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2M9.5 7h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5m3 0h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5M2 10.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5" />
          </svg>
        </div>
      </div>
      {isOpen && renderCalendar()}

      <style jsx>{`
        .custom-datepicker-popup {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: #262626;
          border: 1px solid #ced4da;
          border-radius: 0.375rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          z-index: 1000;
          margin-top: 4px;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #e9ecef;
        }

        .nav-button {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          transition: background-color 0.15s ease-in-out;
          color: #ffffff;
        }

        .nav-button:hover {
          background-color: #404040;
        }

        .month-year {
          font-weight: 600;
          font-size: 1.1rem;
          color: #ffffff;
        }

        .calendar-weekdays {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          background-color: #262626;
          border-bottom: 1px solid #e9ecef;
        }

        .weekday {
          padding: 0.5rem;
          text-align: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #ffffff;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          padding: 0.5rem;
        }

        .calendar-day {
          padding: 0.5rem;
          text-align: center;
          cursor: pointer;
          border-radius: 0.25rem;
          transition: all 0.15s ease-in-out;
          font-size: 0.875rem;
          color: #ffffff;
        }

        .calendar-day:hover {
          background-color: #404040;
        }

        .calendar-day.selected {
          background-color: #0ad6b6;
          color: white;
        }

        .calendar-day.today {
          font-weight: 600;
          color: #0ad6b6;
        }

        .calendar-day.today.selected {
          color: white;
        }

        .calendar-day.empty {
          cursor: default;
        }

        .calendar-day.empty:hover {
          background-color: transparent;
        }

        .calendar-day.disabled {
          color: #666666;
          cursor: not-allowed;
        }

        .calendar-day.disabled:hover {
          background-color: transparent;
        }
        .calendar-day.blocked {
          color: #ff8585;
        }

        .calendar-footer {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          border-top: 1px solid #e9ecef;
          background-color: #262626;
        }

        .footer-button {
          background: none;
          border: none;
          color: #0ad6b6;
          cursor: pointer;
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          transition: background-color 0.15s ease-in-out;
        }

        .footer-button:hover {
          background-color: #555555;
        }

        .custom-datepicker-container input.is-invalid {
          background-image: none;
          padding-right: 2.5rem;
        }

        .calendar-icon {
          transition: color 0.15s ease-in-out;
        }

        .calendar-icon:hover {
          color: #0ad6b6;
        }
      `}</style>
    </div>
  );
}

export default CustomDatePicker;
